import { Component, OnInit } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { WebsiteConfigrationService } from '../../services/website/website-configration.service';
import type { GalleryImage } from '../../services/website/website-configration.service';
import { ActivatedRoute } from '@angular/router';
import { HttpEventType } from '@angular/common/http';
import { AlertBoxComponent } from 'src/app/shared/components/alert-box/alert-box.component';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { extractErrorMessage } from 'src/app/core/services/uitls/error.util';
import { SummerNoteConfig } from 'src/app/common/summernote-config';
import { LocationsService } from 'src/app/core/services/locations/locations.service';
import { ConfirmationBoxComponent } from 'src/app/shared/components/confirmation-box/confirmation-box.component';
import { UserType } from 'src/app/models/enums/user-type.enum';
import { UserService } from 'src/app/core/services/user/user.service';

@Component({
    selector: 'app-website-configrations',
    templateUrl: './website-configrations.component.html',
    styleUrl: './website-configrations.component.scss',
    standalone: false
})
export class WebsiteConfigrationsComponent implements OnInit {
  tags: any[] = [];
  serviceTags: any[] = [];
  classifications: any[] = [];
  paymentMethods: any[] = [];
  configPayementMethods: [] = [];
  servicePaymentMethods: any[] = [];
  termsConditions: any[] = [];
  serviceId: string = '';
  errorMsg: boolean = false;
  message: string;
  serviceName: string = '';
  summerNoteConfig = SummerNoteConfig.MAIN_CONFIG;
  reportImage = {
    name: 'صورة نموذج التقرير',
    fileLabel: null,
    progress: false,
    progressVal: 0,
    file: {},
  };
  submitted: boolean = false;

  websiteConfigFormGroup: FormGroup = new FormGroup({
    show_on_website: new FormControl(false),
    is_city_coverage_enforced: new FormControl(false),
    show_in_government_section: new FormControl(false),
    short_description: new FormControl(null, Validators.required),
    long_description: new FormControl(null, Validators.required),
    tags: new FormControl([], Validators.required),
    classification: new FormControl(null, Validators.required),
    payment_methods_IDs: new FormControl([], Validators.required),
    terms_and_conditions_template: new FormControl(null),
    payment_methods: new FormArray([]),
    price: new FormControl(null, Validators.required),
    pricing_hint: new FormControl(null, Validators.required),
    time_description: new FormControl(null, Validators.required),
    service_time_hint: new FormControl(null, Validators.required),
    report_sample_image: new FormControl(null),
    report_sample_link: new FormControl(null),
    secondary_tags: new FormControl([]),
    service_includes: new FormControl([]),
    service_excludes: new FormControl([]),
    faqs: new FormControl([]),
    regions: new FormControl([]),
    cities: new FormControl([]),
    gallery_images: new FormControl([]),
  });
  saveDialogRef: any;
  deleteDialogRef: MatDialogRef<ConfirmationBoxComponent>;
  regions: any[] = [];
  cities: any[] = [];
  savedRegionIDs: any[] = [];
  savedCityIDs: any[] = [];
  websiteconfigurationHub: any;

  constructor(
    private websiteConfigrationService: WebsiteConfigrationService,
    private route: ActivatedRoute,
    private dialog: MatDialog,
    private locationService: LocationsService,
    private userService: UserService,
  ) {}

  ngOnInit(): void {
    this.errorMsg = false;
    this.message = '';
    this.serviceId = this.route.snapshot.params.id;
    this.getServiceOptions();
    this.getWebsiteConfigration();
    this.getAllRegions();
    this.websiteConfigFormGroup
      .get('is_city_coverage_enforced')
      ?.valueChanges.subscribe((value) => {
        this.toggleCityValidators(value);
      });

    // Reset error message when form becomes valid
    this.websiteConfigFormGroup.statusChanges.subscribe((status) => {
      if (status === 'VALID' && this.submitted) {
        this.errorMsg = false;
      }
    });
  }

  getWebsiteConfigration() {
    this.websiteConfigrationService
      .getWebsiteConfigrationByServiceID(this.serviceId)
      .subscribe({
        next: (res) => {
          this.websiteconfigurationHub = res;
          this.patchWebsiteConfigration(res);
          this.reportImage['fileLabel'] = res.report_sample_image;
          this.configPayementMethods = res.payment_methods;
          this.patchPaymentMethods(res.payment_methods);
          this.setPaymentMethods(res.payment_methods);
        },
        error: (err) => {
          console.error('Error fetching website configuration:', err);
        },
      });
  }

  patchWebsiteConfigration(res) {
    this.serviceTags = [...res.tags];
    this.serviceName = res.name;
    this.savedRegionIDs = res.regions.map((element) => element.id) || [];
    this.savedCityIDs = res.cities.map((element) => element.pk) || [];

    // this.cityRegionMap.clear();
    this.cities = [];

    if (this.savedRegionIDs.length > 0) {
      this.getCitiesByRegion(this.savedRegionIDs);
    }

    this.websiteConfigFormGroup.patchValue({
      show_on_website: res.show_on_website,
      is_city_coverage_enforced: res.is_city_coverage_enforced,
      show_in_government_section: res.show_in_government_section,
      short_description: res.short_description,
      long_description: res.long_description,
      tags: res.tags,
      classification: res.classification,
      terms_and_conditions_template: res.terms_and_conditions_template,
      price: res.price,
      pricing_hint: res.pricing_hint,
      time_description: res.time_description,
      service_time_hint: res.service_time_hint,
      report_sample_image: res.report_sample_image,
      report_sample_link: res.report_sample_link,
      secondary_tags: res.secondary_tags,
      service_includes: res.service_includes,
      service_excludes: res.service_excludes,
      faqs: res.faqs,
      cities: this.savedCityIDs,
      regions: this.savedRegionIDs,
      gallery_images: res.gallery_images || [],
    });
    this.toggleCityValidators(res.is_city_coverage_enforced);
  }

  toggleCityValidators(isEnforced: boolean) {
    const regionsControl = this.websiteConfigFormGroup.get('regions');
    const citiesControl = this.websiteConfigFormGroup.get('cities');

    if (isEnforced) {
      regionsControl?.setValidators([Validators.required]);
      citiesControl?.setValidators([Validators.required]);
    } else {
      regionsControl?.clearValidators();
      citiesControl?.clearValidators();

      regionsControl?.setValue([]);
      citiesControl?.setValue([]);

      this.cities = [];
      this.savedRegionIDs = [];
      this.savedCityIDs = [];
    }

    regionsControl?.updateValueAndValidity();
    citiesControl?.updateValueAndValidity();
  }

  patchPaymentMethods(payment_methods: any[]) {
    this.servicePaymentMethods = payment_methods.map(
      (method) => method.payment_method_id,
    );
    this.websiteConfigFormGroup.patchValue({
      payment_methods_IDs: this.servicePaymentMethods,
    });
  }

  setPaymentMethods(selectedMethods: any[]): void {
    const formArray = this.paymentMethodsFormArray;

    const selectedIds = selectedMethods.map(
      (method) => method.id ?? method.payment_method_id,
    );

    for (let i = formArray.length - 1; i >= 0; i--) {
      const ctrl = formArray.at(i);
      if (!selectedIds.includes(ctrl.value.payment_method_id)) {
        formArray.removeAt(i);
      }
    }

    selectedMethods.forEach((method) => {
      const id = method.id ?? method.payment_method_id;
      const exists = formArray.controls.some(
        (ctrl) => ctrl.value.payment_method_id === id,
      );

      if (!exists) {
        formArray.push(this.createPaymentMethod(method));
      }
    });
  }

  createPaymentMethod(method?: any): FormGroup {
    return new FormGroup({
      payment_method_id: new FormControl(
        method?.payment_method_id ?? method?.id,
        Validators.required,
      ),
      name: new FormControl(method?.name ?? null),
      description: new FormControl(
        method?.description ?? null,
        Validators.required,
      ),
      discount: new FormControl(method?.discount ?? 0, Validators.required),
      expert_description: new FormControl(
        method?.expert_description ?? null,
        Validators.required,
      ),
    });
  }

  get paymentMethodsFormArray(): FormArray {
    return this.websiteConfigFormGroup.get('payment_methods') as FormArray;
  }

  getServiceOptions() {
    this.websiteConfigrationService.getServiceOptions().subscribe({
      next: (res) => {
        this.tags = this.flattenTags(res.service_tags);
        this.classifications = [...res.classifications];
        this.paymentMethods = [...res.payment_methods];
        this.termsConditions = [...res.terms_and_conditions_templates];
      },
      error: (err) => {
        console.error('Error fetching service options:', err);
      },
    });
  }

  getPaymentMethodById(id: number) {
    return this.paymentMethods.find((method) => method.id === id);
  }

  isActiveValueChanged(event) {
    this.websiteConfigFormGroup.get(event.labelEn).setValue(event.value);
  }

  setFaqsValues(event) {
    this.setFormControlValue('faqs', event);
  }

  setValues(event) {
    for (const key in event) {
      if (event.hasOwnProperty(key)) {
        this.setFormControlValue(key, event[key as keyof typeof event]);
      }
    }
  }

  setFormControlValue(formControl: string, value: any[]) {
    this.websiteConfigFormGroup.get(formControl)?.setValue(value);

    if (formControl === 'payment_methods_IDs') {
      const selected = this.paymentMethods.filter((method) =>
        value.includes(method.id),
      );
      this.setPaymentMethods(selected);
    }

    if (formControl === 'region_IDs') {
      this.savedRegionIDs = value;
    }
  }

  deleteImage(event) {
    let formData = new FormData();
    formData.append('report_sample_image', '');
    this.updateServiceConfiguration(formData, true);
  }

  handleImage(event) {
    let formData = new FormData();
    formData.append('report_sample_image', event.files[0]);
    this.reportImage['progress'] = true;
    this.updateServiceConfiguration(formData, event.files[0], true);
  }

  updateServiceConfiguration(data, file?, withProgress?, buttonId?) {
    this.websiteConfigrationService
      .updateWebsiteConfigurationByServiceID(
        this.serviceId,
        data,
        withProgress,
        buttonId,
      )
      .subscribe({
        next: (event) => {
          if (!withProgress && event.type !== HttpEventType.Response) {
            this.showAlertBox(true, 'تم الحفظ بنجاح');
            this.patchWebsiteConfigration(event);
            return;
          }

          switch (event.type) {
            case HttpEventType.Sent:
              this.reportImage.progress = false;
              break;
            case HttpEventType.UploadProgress:
              this.reportImage.progressVal = Math.round(
                (event.loaded / event.total) * 100,
              );
              this.reportImage.progress = true;
              break;
            case HttpEventType.Response:
              this.reportImage.progress = false;
              this.reportImage.file = file;
              this.reportImage['fileLabel'] = event.body.report_sample_image;
              setTimeout(() => {
                this.reportImage.progressVal = 0;
              }, 0);
          }
        },
        error: (error) => {
          const errorMsg = extractErrorMessage(error);
          this.showAlertBox(false, errorMsg);
        },
      });
  }

  closeError() {
    this.errorMsg = false;
  }

  save(event) {
    this.submitted = true;
    this.errorMsg = false;
    if (this.websiteConfigFormGroup.invalid) {
      this.errorMsg = true;
      this.message = 'برجاء تعبئة  جميع الخانات ';
      this.websiteConfigFormGroup.markAllAsTouched();
      return;
    }
    const { report_sample_image, ...value } = this.websiteConfigFormGroup.value;
    this.updateServiceConfiguration(value, undefined, false, event.buttonId);
  }

  showAlertBox(successImg, confirmMsg) {
    this.saveDialogRef = this.dialog.open(AlertBoxComponent, {
      data: {
        imgSuccess: successImg,
        message: confirmMsg,
        button: 'تم',
      },
    });
  }

  getAllRegions() {
    this.locationService.getAllRegions().subscribe({
      next: (res) => {
        this.regions = res;
      },
      error: (err) => {
        console.error('Error fetching regions:', err);
      },
    });
  }

  getCitiesByRegion(regionIds: number[]) {
    if (!regionIds || regionIds.length === 0) return;

    this.locationService.getCoveredCities(regionIds).subscribe({
      next: (res) => {
        this.cities = res;

        this.savedCityIDs = this.savedCityIDs.filter((cityId) =>
          this.cities.some((city) => city.pk === cityId),
        );

        this.websiteConfigFormGroup.get('cities')?.setValue(this.savedCityIDs);
      },
      error: (err) => {
        console.error('Error fetching cities:', err);
      },
    });
  }

  onRegionSelected(selectedRegions: number[]) {
    this.savedRegionIDs = selectedRegions;
    this.websiteConfigFormGroup.get('regions')?.setValue(selectedRegions);

    if (!selectedRegions || selectedRegions.length === 0) {
      this.cities = [];
      this.savedCityIDs = [];
      this.websiteConfigFormGroup.get('cities')?.setValue([]);
      return;
    }

    this.getCitiesByRegion(selectedRegions);
  }

  onCitiesSelected(selectedCities: number[]) {
    this.websiteConfigFormGroup.get('cities')?.setValue(selectedCities);
    if (selectedCities?.length) {
      this.savedCityIDs = selectedCities;
    } else {
      this.savedCityIDs = [];
    }
  }

  // Gallery event handlers
  onGalleryImagesChanged(images: GalleryImage[]) {
    this.websiteConfigFormGroup.get('gallery_images')?.setValue(images);
  }

  showGalleryError(message: string) {
    this.showError(message);
  }

  // Check if current user can access gallery (Admin or Marketing)
  canAccessGallery(): boolean {
    return this.userService.hasRole([UserType.Admin, UserType.Markting]);
  }

  showError(message: string) {
    this.errorMsg = message?.trim() ? true : false;
    this.message = message;
  }

  /**
   * Flattens hierarchical tags structure into a single-level array.
   * Parents get level: 0, children get level: 1
   */
  private flattenTags(tags: any[]): any[] {
    if (!tags || tags.length === 0) {
      return [];
    }

    const flattened: any[] = [];

    tags.forEach((tag) => {
      // Add parent with level 0
      const { children, ...parentTag } = tag;
      flattened.push({ ...parentTag, level: 0 });

      // Add children with level 1
      if (children && children.length > 0) {
        children.forEach((child) => {
          flattened.push({ ...child, level: 1, parent: tag.id });
        });
      }
    });

    return flattened;
  }
}
